/**
 * Default `property_setting` payload for a deal that enters a pipeline without
 * the detail view ever being opened.
 *
 * The "Add to Pipeline" button on the property detail page hands
 * `useSavedProperties().addToPipeline` the live deal inputs
 * (`PropertyDetailContent.getSavePayload()`), so the promoted deal always lands
 * with a `property_setting` row. The New Deal drawer promotes a heart-saved row
 * straight from a list — there is no mounted detail view to read inputs from, so
 * it seeds the exact same shape here: the snapshot-derived defaults the detail
 * page would have shown on first open (`buildInitialInputs`), flattened for
 * persistence by `toPersistedPatch`, plus the default rehab scope buckets.
 *
 * Kept free of React/Supabase deps so it stays unit-testable; the derivation
 * itself is reused from the detail page so the two entry points can't drift.
 */
import {
  buildDefaultRehabLineItems,
  buildInitialInputs,
  toPersistedPatch,
  toPropertySnapshot,
} from "@/components/find/detail/detail-helpers";
import type { MockProperty } from "@/data/mockProperties";
import type { RehabLineItemsDiff, Strategy } from "@/types/pipeline";

export interface SeedDealSettingsOptions {
  /**
   * Attach the default 6-bucket rehab scope as an insert diff. Pass `false`
   * when the deal already has `rehab_line_items` rows so an existing scope is
   * never duplicated.
   */
  includeRehabScope?: boolean;
}

/**
 * Build the settings patch to persist for a newly-promoted deal. `strategy`
 * decides which strategy block gets its financing flag stamped, matching what
 * the detail page writes for the same strategy.
 */
export function buildSeedDealSettings(
  property: MockProperty,
  strategy: Strategy,
  options: SeedDealSettingsOptions = {},
): Record<string, unknown> {
  const state = buildInitialInputs(toPropertySnapshot(property));
  const patch: Record<string, unknown> = toPersistedPatch(state, strategy);

  if (options.includeRehabScope) {
    const insert: NonNullable<RehabLineItemsDiff["insert"]> =
      buildDefaultRehabLineItems().map((item) => ({
        category: "standard",
        label: item.label,
        amount: item.amount,
      }));
    patch.rehab = { insert, update: [], delete: [] } satisfies RehabLineItemsDiff;
  }

  return patch;
}
